import { Group, Stack } from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  useAgentEventRelatedTasks,
  useAgentFinancialData,
  useAgentSessions,
} from '../../api/events-client/events-client.hooks';
import { useTranslation } from 'react-i18next';
import { FilterPanel } from '../../components/filters/filter-panel';
import { FilterPanelGroupRadio } from '../../components/filters/filter-panel-group-radio';
import { FilterPanelHeader } from '../../components/filters/filter-panel-header';
import { BrandedLoadingOverlay } from '../../components/loader/branded-loader';
import { PageLayout } from '../../components/page-layout/page-layout';
import { formatToISODate } from '../../helpers/date-time-helpers/formatting/formatting';
import { safeParseDateInput } from '../../helpers/date-time-helpers/parsing/parsing';
import { useDocumentTitle } from '../../helpers/general-helpers';
import { useAgenticProcessStore } from '../../store/agentic-process-store/agentic-process-store.hooks';
import { useGlobalStore } from '../../store/global-store/global-store';
import { baseWhite, secondaryBase, skyDark } from '../../styles/design-tokens';
import { Button } from '../../ui-library/button/button';
import { Divider } from '../../ui-library/divider/divider';
import { Icon } from '../../ui-library/icon/icon';
import { H2 } from '../../ui-library/typography/typography';
import { MenuDrivenContainer } from '../menu-driven-container/menu-driven-container';
import { AgenticFinancialsChart } from './agentic-financials/agentic-financials-chart';
import { AgenticFinancialsFilters } from './agentic-financials/agentic-financials-filters';
import { AgenticProcessSessionsTable } from './agentic-process-sessions-table/agentic-process-sessions-table';
import { getAgenticProcessSessionsTableColumns } from './agentic-process-sessions-table/agentic-process-sessions-table.helpers';
import { useAgenticProcessSessionsFilters } from './agentic-process-sessions.hooks';
import { AgenticProcessTable } from './agentic-process-table/agentic-process-table';
import { getAgenticProcessTableColumns } from './agentic-process-table/agentic-process-table.helpers';
import { AgenticProcessTabs } from './agentic-process-tabs';
import { useAgenticProcessFilters } from './agentic-process.hooks';
import { AgenticViewType } from './agentic-process.type';

export const AgenticProcess = () => {
  const i18n = useTranslation();
  useDocumentTitle('Agentic Process - Bloomfilter');
  const { team, board, filterGroups, handleResetFilters } = useAgenticProcessFilters();
  const navigate = useNavigate();
  const organizationId = useGlobalStore((state) => state.organization?.id);

  const { selectedTab, setSelectedTab, startDate, endDate, setDateRange } = useAgenticProcessStore();
  const [viewType, setViewType] = useState<AgenticViewType>(
    selectedTab ? (selectedTab as AgenticViewType) : AgenticViewType.Tasks,
  );
  const [collapsedRadioGroups, setCollapsedRadioGroups] = useState<Record<string, boolean>>({});

  const navigateToAgenticMap = (taskId: string) => {
    navigate(`/application/agentic-process/graph?taskId=${taskId}`);
  };

  const navigateToSessionMap = (sessionId: string) => {
    navigate(`/application/agentic-process/graph?sessionId=${sessionId}`);
  };

  const initialStartDate = (() => {
    const parsed = safeParseDateInput(startDate);
    if (parsed) {
      const date = parsed.toDate();
      if (date) return date;
    }
    return dayjs().subtract(1, 'month').startOf('month').toDate();
  })();

  const initialEndDate = (() => {
    const parsed = safeParseDateInput(endDate);
    if (parsed) {
      const date = parsed.toDate();
      if (date) return date;
    }
    return dayjs().toDate();
  })();

  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(initialStartDate);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(initialEndDate);
  const [isDatesDirty, setIsDatesDirty] = useState(false);

  const handleDateChange = (value: any) => {
    const startDate = value[0] ? (safeParseDateInput(value[0])?.toDate() ?? null) : null;
    const endDate = value[1] ? (safeParseDateInput(value[1])?.toDate() ?? null) : null;
    setSelectedStartDate(startDate);
    setSelectedEndDate(endDate);
    setIsDatesDirty(true);
  };

  const handleApplyDates = () => {
    const formattedStartDate = selectedStartDate ? formatToISODate(selectedStartDate) : null;
    let formattedEndDate = null;

    if (selectedEndDate) {
      const endDateRaw = dayjs(selectedEndDate);
      const isCurrentMonth = endDateRaw.isSame(dayjs(), 'month');
      formattedEndDate = isCurrentMonth
        ? formatToISODate(dayjs().toDate())
        : formatToISODate(endDateRaw.endOf('month').toDate());
    }

    // Update the store with new date range
    setDateRange(formattedStartDate, formattedEndDate);
    setIsDatesDirty(false);
  };

  // Update tab selection in store when viewType changes
  useEffect(() => {
    setSelectedTab(viewType);
  }, [viewType, setSelectedTab]);

  const { data: tasks, query: tasksQuery } = useAgentEventRelatedTasks({
    projectId: team?.id || '',
    subprojectId: board?.id || '',
    startDate: startDate ? formatToISODate(startDate) : '',
    endDate: endDate ? formatToISODate(endDate) : '',
  });

  const { data: sessions, query: sessionsQuery } = useAgentSessions({
    organizationId: organizationId || '',
    startDate: startDate ? formatToISODate(startDate) : '',
    endDate: endDate ? formatToISODate(endDate) : '',
  });

  const { data: agentFinancialData, query: agentFinancialDataQuery } = useAgentFinancialData({
    organizationId: organizationId || '',
    startDate: startDate ? formatToISODate(startDate) : '',
    endDate: endDate ? formatToISODate(endDate) : '',
  });

  // Sessions filters
  const {
    filteredSessions,
    filterGroups: sessionsFilterGroups,
    handleResetFilters: handleResetSessionsFilters,
  } = useAgenticProcessSessionsFilters(sessions || []);

  const header = (
    <Group>
      <Icon name="assignment_ind" color={secondaryBase} size={36} />
      <H2>{`${i18n.t('navigation.agentic_process')}`}</H2>
      <MonthPickerInput
        type="range"
        radius="xl"
        style={{ width: '300px' }}
        leftSection={<Icon name="calendar_month" color={secondaryBase} size={16} />}
        leftSectionPointerEvents="none"
        placeholder="Pick date range"
        value={[selectedStartDate, selectedEndDate]}
        onChange={handleDateChange}
        maxDate={dayjs().toDate()}
        clearable
      />
      <Button
        onClick={handleApplyDates}
        variant="secondary"
        radius="xl"
        size="xs"
        disabled={!isDatesDirty}
        style={{ border: !isDatesDirty ? `1px solid ${skyDark}` : 'none' }}
      >
        Apply Dates
      </Button>
      {selectedTab === AgenticViewType.Financials && (
        <Button
          onClick={() => navigate('/application/agentic-process/edit-agentic-financial-data')}
          variant="primary"
          radius="xl"
          size="xs"
          leftSection={<Icon name="edit" color={baseWhite} size={16} />}
        >
          Edit Financial Data
        </Button>
      )}
    </Group>
  );

  const agenticProcessTableColumns = getAgenticProcessTableColumns(navigateToAgenticMap, i18n.t);
  const agenticProcessSessionsTableColumns = getAgenticProcessSessionsTableColumns();

  const isLoading = tasksQuery.isLoading || sessionsQuery.isLoading || agentFinancialDataQuery.isLoading;

  const toggleRadioGroup = (groupName: string) => {
    setCollapsedRadioGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  const renderMainSection = () => {
    switch (viewType) {
      case AgenticViewType.Tasks:
        return <AgenticProcessTable tasks={tasks || []} columns={agenticProcessTableColumns} />;
      case AgenticViewType.Sessions:
        return (
          <AgenticProcessSessionsTable
            sessions={filteredSessions}
            columns={agenticProcessSessionsTableColumns}
            navigateToSessionMap={navigateToSessionMap}
          />
        );
      case AgenticViewType.Financials:
        return (
          <AgenticFinancialsChart
            data={agentFinancialData}
            isPending={agentFinancialDataQuery.isLoading || agentFinancialDataQuery.isFetching}
          />
        );
      default:
        return <AgenticProcessTable tasks={tasks || []} columns={agenticProcessTableColumns} />;
    }
  };

  const renderSideFilters = () => {
    switch (viewType) {
      case AgenticViewType.Tasks:
        return {
          content: (
            <FilterPanel filterGroups={filterGroups} onResetFilters={handleResetFilters} useSelectFilters={true} />
          ),
        };
      case AgenticViewType.Sessions:
        return {
          content: (
            <Stack gap="md">
              <FilterPanelHeader onResetFilters={handleResetSessionsFilters} />
              <Divider />
              {sessionsFilterGroups.map((group) => (
                <FilterPanelGroupRadio
                  key={group.name}
                  group={group}
                  toggleCollapsed={() => toggleRadioGroup(group.name)}
                  isCollapsed={collapsedRadioGroups[group.name] || false}
                  hasDivider={group !== sessionsFilterGroups[sessionsFilterGroups.length - 1]}
                />
              ))}
            </Stack>
          ),
        };
      case AgenticViewType.Financials:
        return {
          content: (
            <AgenticFinancialsFilters
              data={agentFinancialData}
              isPending={agentFinancialDataQuery.isLoading || agentFinancialDataQuery.isFetching}
            />
          ),
        };
      default:
        return undefined;
    }
  };

  return (
    <MenuDrivenContainer header={header}>
      <BrandedLoadingOverlay visible={isLoading} variant="colored" />
      <PageLayout
        controlBar={undefined}
        tabs={<AgenticProcessTabs viewType={viewType} setViewType={setViewType} />}
        sideFilters={renderSideFilters()}
        mainSection={renderMainSection()}
      />
    </MenuDrivenContainer>
  );
};
